import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { SharedModule } from '../../shared/shared.module';
import { ActiveCallInfo } from '../../shared/services/sip/sip.service';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import * as FontAwesomeIcons from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';

export interface VideoControlsState {
  isVideoMuted: boolean;
  isAudioMuted: boolean;
  isFullscreen: boolean;
  selectedCameraId?: string;
}

@Component({
  selector: 'app-video-controls',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './video-controls.component.html',
  styleUrls: ['./video-controls.component.scss']
})
export class VideoControlsComponent implements OnInit {
  @Input() call: ActiveCallInfo | null = null;
  @Input() availableCameras: MediaDeviceInfo[] = [];
  @Input() autoHide: boolean = true;
  @Input() alwaysVisible: boolean = false;

  @Output() videoMuteToggle = new EventEmitter<void>();
  @Output() audioMuteToggle = new EventEmitter<void>();
  @Output() cameraSwitch = new EventEmitter<string>();
  @Output() fullscreenToggle = new EventEmitter<void>();
  @Output() undockRequest = new EventEmitter<void>();
  @Output() endCall = new EventEmitter<void>();
  @Output() visibilityChange = new EventEmitter<boolean>();

  // UI state
  private _isControlsVisible = true;
  get isControlsVisible(): boolean {
    return this._isControlsVisible;
  }
  set isControlsVisible(value: boolean) {
    if (this._isControlsVisible !== value) {
      this._isControlsVisible = value;
      this.visibilityChange.emit(value);
    }
  }
  hideTimer: any;

  // Font Awesome icons
  faVideo = FontAwesomeIcons.faVideo;
  faVideoSlash = FontAwesomeIcons.faVideoSlash;
  faMicrophone = FontAwesomeIcons.faMicrophone;
  faMicrophoneSlash = FontAwesomeIcons.faMicrophoneSlash;
  faPhoneSlash = FontAwesomeIcons.faPhoneSlash;
  faSyncAlt = FontAwesomeIcons.faSyncAlt;
  faExpand = FontAwesomeIcons.faExpand;
  faExternalLinkAlt = FontAwesomeIcons.faExternalLinkAlt;
  faInfoCircle = FontAwesomeIcons.faInfoCircle;

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    if (this.autoHide && !this.alwaysVisible) {
      this.startHideTimer();
    }
  }

  /**
   * Show controls on mouse movement
   */
  onMouseMove(): void {
    if (!this.alwaysVisible) {
      this.isControlsVisible = true;
      this.startHideTimer();
    }
  }

  /**
   * Start timer to auto-hide controls
   */
  private startHideTimer(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
    }

    if (this.autoHide) {
      this.hideTimer = setTimeout(() => {
        this.isControlsVisible = false;
      }, 3000); // Hide after 3 seconds of inactivity
    }
  }

  /**
   * Toggle video mute
   */
  toggleVideoMute(): void {
    this.videoMuteToggle.emit();
  }

  /**
   * Toggle audio mute
   */
  toggleAudioMute(): void {
    this.audioMuteToggle.emit();
  }

  /**
   * Switch camera device
   */
  switchCamera(deviceId: string): void {
    this.cameraSwitch.emit(deviceId);
  }

  /**
   * Toggle fullscreen
   */
  toggleFullscreen(): void {
    this.fullscreenToggle.emit();
  }

  /**
   * Request undock to separate window
   */
  requestUndock(): void {
    this.undockRequest.emit();
  }

  /**
   * End the call
   */
  endVideoCall(): void {
    this.endCall.emit();
  }

  /**
   * Get icon for video mute button
   */
  get videoMuteIcon(): IconDefinition {
    return this.call?.isVideoMuted ? this.faVideoSlash : this.faVideo;
  }

  /**
   * Get icon for audio mute button
   */
  get audioMuteIcon(): IconDefinition {
    return this.call?.isMuted ? this.faMicrophoneSlash : this.faMicrophone;
  }

  /**
   * Get tooltip for video mute button
   */
  get videoMuteTooltip(): string {
    return this.call?.isVideoMuted
      ? this.translate.instant('ASTIMAX.PHONE.WIDGET.VIDEO.CONTROLS.TURN_CAMERA_ON')
      : this.translate.instant('ASTIMAX.PHONE.WIDGET.VIDEO.CONTROLS.TURN_CAMERA_OFF');
  }

  /**
   * Get tooltip for audio mute button
   */
  get audioMuteTooltip(): string {
    return this.call?.isMuted
      ? this.translate.instant('ASTIMAX.PHONE.WIDGET.VIDEO.CONTROLS.UNMUTE_MICROPHONE')
      : this.translate.instant('ASTIMAX.PHONE.WIDGET.VIDEO.CONTROLS.MUTE_MICROPHONE');
  }

  /**
   * Get CSS class for control bar visibility
   */
  get controlsVisibilityClass(): string {
    return this.isControlsVisible || this.alwaysVisible ? 'visible' : 'hidden';
  }
}
